import { useCallback, useEffect, useState } from 'react';
import { authStore } from '@/stores';
import { getApps, initializeApp } from 'firebase/app';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { AnalyticEvent, AnalyticsEvents, UserDto } from '@sweatpals/shared';
import { CustomEventName, logEvent, setUserId, setUserProperties } from '@firebase/analytics';

/**
 * Hook for Firebase Analytics integration. Initializes automatically on first usage and acts as a singleton.
 * Provides typed event logging and handles user identification.
 *
 * @remarks
 * Requires the following environment variables to be set for initialization:
 * - NEXT_PUBLIC_FIREBASE_API_KEY
 * - NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN
 * - NEXT_PUBLIC_FIREBASE_PROJECT_ID
 * - NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET
 * - NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID
 * - NEXT_PUBLIC_FIREBASE_APP_ID
 * - NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID
 *
 * @returns An object containing:
 * - isAnalyticsSupported: boolean indicating if Firebase Analytics is supported in current environment
 * - logEventTyped: Function to log typed analytics events with metadata
 * - setUserIdExternally: Function to set user ID externally
 *
 * @example
 * ```tsx
 * const { logEventTyped, isAnalyticsSupported } = useFirebaseAnalytics();
 *
 * // Log a typed event
 * logEventTyped('user_action', {
 *   userId: '123',
 *   action: 'click'
 * });
 * ```
 */
export const useFirebaseAnalytics = () => {
	const [isAnalyticsSupported, setIsAnalyticsSupported] = useState<boolean>(false);
	const currentUser = authStore.user;

	useEffect(() => {
		const firebaseConfig = {
			apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
			authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
			projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
			storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
			messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
			appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
			measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
		};

		if (typeof window !== undefined) {
			if (!getApps().length) {
				const app = initializeApp(firebaseConfig);
				getAnalytics(app);
			}

			isSupported()
				.then(supported => {
					console.info('[Firebase] is supported: ', supported);
					setIsAnalyticsSupported(supported);
				});
		}
	}, []);

	const setCurrentUserId = useCallback((currentUser: UserDto) => {
		try {
			const analytics = getAnalytics(getApps()[0]);
			setUserId(analytics, currentUser.id);
			setUserProperties(analytics, { ...currentUser });

			console.info('[Firebase] User id set: ', currentUser.id);
		} catch (e) {
			console.error('[Firebase] Error while setting user id', e);
		}
	}, []);

	useEffect(() => {
		if (currentUser?.id && isAnalyticsSupported) {
			setCurrentUserId(currentUser);
		}
	}, [isAnalyticsSupported, setCurrentUserId, currentUser?.id]);

	const logEventTyped = useCallback(<T extends CustomEventName<AnalyticsEvents>>(event: T, metadata: AnalyticEvent<T>) => {
		try {
			metadata = {
				...metadata,
				platform: 'web',
				projectPart: 'landing',
				data_stream: 'landing'
			}

			const analytics = getAnalytics(getApps()[0]);
			logEvent<AnalyticsEvents>(analytics, event, metadata);

			console.info('[Firebase] Event logged: ', { event, metadata });
		} catch (e) {
			console.error('[Firebase] Error while logging event', e);
		}
	}, []);

	const setUserIdExternally = useCallback((userId: string) => {
		try {
			const analytics = getAnalytics();
			setUserId(analytics, userId);

			console.info('[Firebase] User id set: ', userId);
		} catch (e) {
			console.error('[Firebase] Error while setting user id', e);
		}
	}, []);

	return {
		isAnalyticsSupported,
		logEventTyped,
		setUserIdExternally,
	};
}
